import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldWarning, CircleNotch, CheckCircle, XCircle } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComplianceBar = ({ npi }) => {
  const [pecos, setPecos] = useState({ status: "loading" });
  const [oig, setOig] = useState({ status: "loading" });

  useEffect(() => {
    if (!npi) return;
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const [pecosRes, oigRes] = await Promise.all([
          fetch(`${API_URL}/api/pecos/check/${npi}`).then(r => r.json()).catch(() => ({ status: "unavailable" })),
          fetch(`${API_URL}/api/oig/check/${npi}`).then(r => r.json()).catch(() => ({ status: "unavailable" })),
        ]);
        if (cancelled) return;
        setPecos(pecosRes);
        setOig(oigRes);
      } catch {
        if (!cancelled) {
          setPecos({ status: "unavailable" });
          setOig({ status: "unavailable" });
        }
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [npi]);

  const isLoading = pecos.status === "loading" || oig.status === "loading";

  if (isLoading) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-3" data-testid="compliance-bar">
        <CircleNotch className="w-5 h-5 text-slate-400 animate-spin shrink-0" />
        <span className="text-sm text-slate-500">Checking compliance status...</span>
      </div>
    );
  }

  const pecosEnrolled = pecos.enrolled === true;
  const oigExcluded = oig.excluded === true;
  const hasIssue = oigExcluded;

  return (
    <div
      className={`rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3 border ${
        hasIssue
          ? "bg-red-50 border-red-200"
          : "bg-emerald-50/50 border-emerald-200"
      }`}
      data-testid="compliance-bar"
    >
      <div className="flex items-center gap-2 shrink-0">
        {hasIssue ? (
          <ShieldWarning className="w-5 h-5 text-red-600" weight="fill" />
        ) : (
          <ShieldCheck className="w-5 h-5 text-emerald-600" weight="fill" />
        )}
        <span className={`text-sm font-semibold ${hasIssue ? "text-red-700" : "text-emerald-700"}`}>
          Compliance Overview
        </span>
      </div>

      <div className="flex flex-wrap gap-3 text-sm">
        {/* PECOS Status */}
        <Link
          to={`/pecos-lookup/npi/${npi}`}
          className="inline-flex items-center gap-1.5 hover:underline"
          data-testid="compliance-pecos"
        >
          {pecosEnrolled ? (
            <CheckCircle className="w-4 h-4 text-emerald-600" weight="fill" />
          ) : pecos.enrolled === false ? (
            <XCircle className="w-4 h-4 text-amber-500" weight="fill" />
          ) : (
            <CircleNotch className="w-4 h-4 text-slate-400" />
          )}
          <span className={`font-medium ${pecosEnrolled ? "text-emerald-700" : pecos.enrolled === false ? "text-amber-600" : "text-slate-500"}`}>
            PECOS: {pecosEnrolled ? "Enrolled" : pecos.enrolled === false ? "Not Found" : "N/A"}
          </span>
        </Link>

        <span className="text-slate-300">|</span>

        {/* OIG Status */}
        <Link
          to="/oig-exclusion-lookup"
          className="inline-flex items-center gap-1.5 hover:underline"
          data-testid="compliance-oig"
        >
          {oigExcluded ? (
            <ShieldWarning className="w-4 h-4 text-red-600" weight="fill" />
          ) : oig.excluded === false ? (
            <ShieldCheck className="w-4 h-4 text-emerald-600" weight="fill" />
          ) : (
            <CircleNotch className="w-4 h-4 text-slate-400" />
          )}
          <span className={`font-medium ${oigExcluded ? "text-red-700" : oig.excluded === false ? "text-emerald-700" : "text-slate-500"}`}>
            OIG: {oigExcluded ? "EXCLUDED" : oig.excluded === false ? "Clear" : "N/A"}
          </span>
        </Link>
      </div>
    </div>
  );
};

export default ComplianceBar;
